import { type MaintenanceInkPoint, type MaintenanceInkStroke } from '@gb-ticket/shared-types';

export const INK_ZOOM_MIN = 1;
export const INK_ZOOM_MAX = 3;
export const PALM_CONTACT_PX = 40;
export const POINT_DEDUP = 0.002;
export const MAX_STROKE_POINTS = 4000;
/** Screen-circle radius in page-height units. Tighter than the old 0.03 unit box. */
export const ERASER_RADIUS = 0.012;

export type InkTool = 'draw' | 'erase' | 'scroll' | 'text';
export type InkPointerType = 'pen' | 'touch' | 'mouse';
export type InkPointerRole = 'draw' | 'erase' | 'pan' | 'pinch' | 'pending' | 'ignore' | 'text';

export type PanOrigin = {
  scrollLeft: number;
  scrollTop: number;
  clientX: number;
  clientY: number;
};

export function normalizePointerType(value: string): InkPointerType {
  if (value === 'pen' || value === 'touch') {
    return value;
  }
  return 'mouse';
}

export function isLargeContact(width: number, height: number): boolean {
  return Math.max(width, height) >= PALM_CONTACT_PX;
}

/**
 * One new pointer, given how many touches are already on the page.
 * A second touch is always a pinch. A lone touch while a pen is down waits,
 * so a palm does not draw and a second finger can still zoom.
 */
export function roleForNewPointer(input: {
  pointerType: InkPointerType;
  tool: InkTool;
  penSeen: boolean;
  penDown: boolean;
  trackedTouchCount: number;
  largeContact: boolean;
}): InkPointerRole {
  if (input.pointerType === 'touch' && input.largeContact) {
    return 'ignore';
  }
  if (input.pointerType === 'touch' && input.trackedTouchCount >= 1) {
    return 'pinch';
  }
  if (input.pointerType === 'touch' && input.penDown) {
    return 'pending';
  }
  if (input.tool === 'scroll') {
    return 'pan';
  }
  if (input.pointerType === 'touch' && input.penSeen) {
    return 'pan';
  }
  if (input.tool === 'erase') {
    return 'erase';
  }
  if (input.tool === 'text') {
    return 'text';
  }
  return 'draw';
}

export function shouldCapturePointer(pointerType: InkPointerType, role: InkPointerRole): boolean {
  if (pointerType === 'touch') {
    return false;
  }
  return role === 'draw' || role === 'erase' || role === 'pan' || role === 'text';
}

export function shouldCancelFingerInk(existingTouchRoles: InkPointerRole[]): boolean {
  return existingTouchRoles.some((role) => role === 'draw' || role === 'erase');
}

export function clampZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) {
    return INK_ZOOM_MIN;
  }
  return Math.min(INK_ZOOM_MAX, Math.max(INK_ZOOM_MIN, zoom));
}

export function inkPageWidth(zoom: number): string {
  return `${clampZoom(zoom) * 100}%`;
}

export function zoomFromPinch(startZoom: number, startDistance: number, distance: number): number {
  if (startDistance <= 0) {
    return clampZoom(startZoom);
  }
  return clampZoom(startZoom * (distance / startDistance));
}

export function scrollForPan(
  origin: PanOrigin,
  clientX: number,
  clientY: number,
): { scrollLeft: number; scrollTop: number } {
  return {
    scrollLeft: origin.scrollLeft - (clientX - origin.clientX),
    scrollTop: origin.scrollTop - (clientY - origin.clientY),
  };
}

/** Keeps the content point under the pinch midpoint after the page block scales around its origin. */
export function scrollToKeepPoint(input: {
  originX: number;
  originY: number;
  contentX: number;
  contentY: number;
  scale: number;
  midX: number;
  midY: number;
  scrollerLeft: number;
  scrollerTop: number;
}): { scrollLeft: number; scrollTop: number } {
  const nextX = input.originX + (input.contentX - input.originX) * input.scale;
  const nextY = input.originY + (input.contentY - input.originY) * input.scale;
  return {
    scrollLeft: nextX - (input.midX - input.scrollerLeft),
    scrollTop: nextY - (input.midY - input.scrollerTop),
  };
}

function roundUnit(value: number): number {
  return Math.round(Math.min(1, Math.max(0, value)) * 10000) / 10000;
}

export function pageAspect(width: number, height: number): number {
  return (width || 1) / (height || 1);
}

export function strokeHit(
  stroke: MaintenanceInkStroke,
  point: MaintenanceInkPoint,
  aspect: number,
): boolean {
  const scaleX = aspect > 0 ? aspect : 1;
  const limit = ERASER_RADIUS * ERASER_RADIUS;
  return stroke.points.some((existing) => {
    const dx = (existing.x - point.x) * scaleX;
    const dy = existing.y - point.y;
    return dx * dx + dy * dy <= limit;
  });
}

export function unitPoint(
  rect: { left: number; top: number; width: number; height: number },
  clientX: number,
  clientY: number,
): MaintenanceInkPoint {
  return {
    x: roundUnit((clientX - rect.left) / (rect.width || 1)),
    y: roundUnit((clientY - rect.top) / (rect.height || 1)),
  };
}

/** Returns a new list when a point was added. The 0.002 dedup and the 4000-point cap stay. */
export function appendStrokePoints(
  points: MaintenanceInkPoint[],
  incoming: MaintenanceInkPoint[],
): MaintenanceInkPoint[] | null {
  const next = points.slice();
  let changed = false;
  for (const point of incoming) {
    if (next.length >= MAX_STROKE_POINTS) {
      break;
    }
    const last = next[next.length - 1];
    if (
      last &&
      Math.abs(last.x - point.x) < POINT_DEDUP &&
      Math.abs(last.y - point.y) < POINT_DEDUP
    ) {
      continue;
    }
    next.push(point);
    changed = true;
  }
  return changed ? next : null;
}
